import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import FullPageLoader from '~components/FullPageLoader';

import { getSettings } from '~main/actions/login';

import { selectAuthenticated, selectUserTags } from '~main/selectors/login';

// Single-use handoff ticket minted by tp-v1's tav-go.asp. The legacy tp1-session
// parameter stays accepted until TP1 stops emitting it.
const HANDOFF_PARAM = 'tav-handoff';
const LEGACY_SESSION_PARAM = 'tp1-session';

const PrivateRoute: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handoffKey = searchParams.get(HANDOFF_PARAM);
  const legacySessionKey = searchParams.get(LEGACY_SESSION_PARAM);
  const crossAppKey = handoffKey ?? legacySessionKey;

  // A parameter present with no value reads as '' rather than null, which is falsy. Presence
  // is what matters, not emptiness: the key still arrived from TP1, so it belongs on the
  // redeem path where the endpoint rejects it like any other bad value. Treating it as absent
  // instead would skip redemption entirely and leave the empty parameter in the address bar.
  const isHandoff = handoffKey !== null;
  const hasCrossAppKey = isHandoff || legacySessionKey !== null;

  const isAuthenticated = useAppSelector(selectAuthenticated);
  const tags = useAppSelector(selectUserTags);
  const dispatch = useAppDispatch();

  const tagsLoading = !tags || tags.length === 0;
  const showOutlet = isAuthenticated && !hasCrossAppKey && !tagsLoading;

  useEffect(() => {
    // If a cross-application key is present, redirect to check it via component
    if (hasCrossAppKey) {
      // Strip both forms, so no key survives into the route the user lands on.
      searchParams.delete(HANDOFF_PARAM);
      searchParams.delete(LEGACY_SESSION_PARAM);
      const searchParamString = searchParams.toString();
      const fromUrl = location.pathname + (searchParamString ? `?${searchParamString}` : '');
      navigate('/auth-session', {
        state: { from: fromUrl, sessionKey: crossAppKey, isHandoff },
      });
    } else {
      dispatch(getSettings());
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated && !hasCrossAppKey) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [isAuthenticated]);

  return (
    <>
      <FullPageLoader isLoading={tagsLoading} />
      {showOutlet && <Outlet />}
    </>
  );
};

export default PrivateRoute;
